import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getLegacyPatients } from '../../util/patientsApi';
import { getAllLegacyTrialsByUser } from '../../util/reportAPI';
import {
  comparePatientNames,
  formatLegacyDate,
  legacyDashboardPath,
  summarizeLegacyPatients,
} from '../../util/legacyAnalytics';
import Spinner from '../decorations/Spinner';
import '../../../dist/assets/stylesheets/css/patients.css';
import '../../../dist/assets/stylesheets/css/legacyAnalytics.css';

const PAGE_SIZES = [25, 50, 100];

const SORTERS = {
  name: comparePatientNames,
  trials: (a, b) => a.trialCount - b.trialCount,
  last: (a, b) => (a.lastMs ?? 0) - (b.lastMs ?? 0),
};

function SortHeader({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th aria-sort={active ? (sort.ascending ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className="legacy-sort-btn"
        onClick={() => onSort(sortKey)}
      >
        {label}
        <Icon
          icon={active ? 'fluent:arrow-sort-down-24-filled' : 'basil:sort-solid'}
          vFlip={active && !sort.ascending}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

function LegacyPatientsPage() {
  const [patients, setPatients] = useState(null);
  const [trials, setTrials] = useState([]);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'name', ascending: true });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZES[0]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getLegacyPatients(), getAllLegacyTrialsByUser()]).then(([list, trialsRes]) => {
      if (cancelled) return;
      setPatients(Array.isArray(list) ? list : []);
      setTrials((trialsRes && trialsRes.result) || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const summaries = summarizeLegacyPatients(patients || [], trials).filter((row) =>
      !needle ||
      [row.patient_name, row.patient_notes, row.patient_condition].some((value) =>
        String(value ?? '').toLowerCase().includes(needle)
      )
    );
    const compare = SORTERS[sort.key];
    summaries.sort((a, b) => (sort.ascending ? compare(a, b) : compare(b, a)));
    return summaries;
  }, [patients, trials, query, sort]);

  useEffect(() => {
    setPage(0);
  }, [query, rowsPerPage, sort]);

  const handleSort = (key) => {
    setSort((prev) => (prev.key === key ? { key, ascending: !prev.ascending } : { key, ascending: key === 'name' }));
  };

  if (patients === null) return <Spinner />;

  const total = rows.length;
  const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  const visible = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="parent-container legacy-page">
      <div className="container legacy-page__header">
        <h1 className="page-title">Legacy analytics</h1>
        <p className="page-sub-title legacy-page__subtitle">
          Gait-lab trials imported from 2019–2020. Review and annotate only: these trials have no
          calibration or pipeline inputs, so they cannot be processed.
        </p>
        <p className="legacy-page__totals">
          {patients.length} patients · {trials.length} trials
        </p>
      </div>
      {patients.length === 0 ? (
        <p className="legacy-empty">No legacy datasets on this account.</p>
      ) : (
        <section className="patient-table container">
          <table>
            <tbody>
              <tr>
                <td colSpan="5" style={{ height: 'fit-content' }}>
                  <input
                    id="search-bar"
                    placeholder="Search patients..."
                    aria-label="Search legacy patients"
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <SortHeader label="Patient" sortKey="name" sort={sort} onSort={handleSort} />
                <SortHeader label="Trials" sortKey="trials" sort={sort} onSort={handleSort} />
                <th>Sessions</th>
                <th>First recorded</th>
                <SortHeader label="Last recorded" sortKey="last" sort={sort} onSort={handleSort} />
              </tr>
              {visible.map((row) => {
                const url = legacyDashboardPath(row.patient_id);
                return (
                  <tr className="patient-row" key={row.patient_id}>
                    <td>
                      <Link to={url}>
                        <div className="legacy-patient-name">
                          {row.patient_name || `Patient ${row.patient_id}`}
                          {row.badTriangulationCount > 0 ? (
                            <span
                              className="legacy-flag-count"
                              title={`${row.badTriangulationCount} trial(s) flagged for bad triangulation`}
                            >
                              <Icon icon="mdi:alert-outline" aria-hidden="true" />
                              {row.badTriangulationCount}
                            </span>
                          ) : null}
                        </div>
                      </Link>
                    </td>
                    <td><Link to={url}><div>{row.trialCount}</div></Link></td>
                    <td><Link to={url}><div>{row.sessionCount}</div></Link></td>
                    <td><Link to={url}><div>{formatLegacyDate(row.firstMs, row.timezone)}</div></Link></td>
                    <td><Link to={url}><div>{formatLegacyDate(row.lastMs, row.timezone)}</div></Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="table-pagination">
            <span>Rows per page:</span>
            <select
              value={rowsPerPage}
              aria-label="Rows per page"
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="pagination-range">
              {total === 0
                ? '0-0 of 0'
                : `${page * rowsPerPage + 1}-${Math.min(page * rowsPerPage + rowsPerPage, total)} of ${total}`}
            </span>
            <div className="pagination-actions">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                aria-label="Previous page"
              >
                <Icon icon="mdi:chevron-left" />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage}
                aria-label="Next page"
              >
                <Icon icon="mdi:chevron-right" />
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default LegacyPatientsPage;
